import React, { useContext, useEffect, useState } from 'react'
import { Redirect, Route, RouteChildrenProps, RouteComponentProps, Switch } from 'react-router'

import { AuthContext } from '../../contexts'
import { updateTimeStamp } from '../../utils'

import { Navmenu } from './Navmenu/Navmenu'
import { ScanPage } from '../ScanPage/ScanPage'
import { Scan } from '../Scan'
import { NoPermissions } from './NoPermissions'
import UploadPage from '../UploadPage/UploadPage'
import { AuthenticCheckPage } from '../AuthenticCheckPage/AuthenticCheckPage'
import { BatchTracing, BatchTracingRouteParams } from '../BatchTracing/BatchTracingPage'
import { SerialTracing, SerialTracingPageRouteParams } from '../SerialTracingPage/SerialTracingPage'
import { ManualPage } from '../Manual/ManualPage'
import pageRoleGroups from './Roles'
import GenerateSerialNumbersPage from '../GenerateSerialNumbersPage/GenerateSerialNumbersPage'
import ManageCodesPage from '../ManageCodesPage/ManageCodesPage'

const checkPageAccessPermissions = <T extends object>(hasAccess: boolean, Component: T) =>
  hasAccess ? Component : NoPermissions

export const Layout: React.FunctionComponent<RouteChildrenProps> = React.memo(props => {
  const { userData, hasGroup, userAttributes, refreshAuthData, getGroupDefaultRoute } = useContext(AuthContext)
  const [isScanPageNew, setIsScanPageNew] = useState(false)

  useEffect(() => {
    props.history.listen(() => {
      updateTimeStamp()
    })
  }, [props.history])

  const toggleScanPage = (newValue?: boolean) => {
    if (typeof newValue === 'undefined') {
      setIsScanPageNew(!isScanPageNew)
      return
    }

    setIsScanPageNew(newValue!)
  }

  return (
    <>
      <Navmenu
        {...props}
        userData={userData}
        hasGroup={hasGroup}
        refreshAuthData={refreshAuthData!}
        isScanPageNew={isScanPageNew}
        toggleScanPage={toggleScanPage}
      />
      <Switch>
        <Route
          path="/scan"
          exact
          render={() =>
            checkPageAccessPermissions<JSX.Element>(
              hasGroup(pageRoleGroups.scan),
              isScanPageNew ? (
                <Scan {...props} />
              ) : (
                <ScanPage {...props} userAttributes={userAttributes} userData={userData} hasGroup={hasGroup} />
              ),
            )
          }
        />
        <Route
          path="/upload"
          exact
          render={() => checkPageAccessPermissions<JSX.Element>(hasGroup(pageRoleGroups.upload), <UploadPage />)}
        />
        <Route
          path="/authentic-check"
          exact
          render={() =>
            checkPageAccessPermissions<JSX.Element>(
              hasGroup(pageRoleGroups['authentic-check']),
              <AuthenticCheckPage {...props} hasGroup={hasGroup} userData={userData} />,
            )
          }
        />
        <Route
          path="/batch-numbers/:searchByProperty?/:searchByPropertyValue?"
          exact
          component={checkPageAccessPermissions<React.ComponentType<RouteComponentProps<BatchTracingRouteParams>>>(
            hasGroup(pageRoleGroups['batch-numbers']),
            BatchTracing,
          )}
        />
        <Route
          path="/trace/:type?/:id?"
          exact
          component={checkPageAccessPermissions<React.ComponentType<RouteComponentProps<SerialTracingPageRouteParams>>>(
            hasGroup(pageRoleGroups.trace),
            SerialTracing,
          )}
        />
        <Route
          path="/generate-serials"
          exact
          component={checkPageAccessPermissions(
            hasGroup(pageRoleGroups['generate-serials']),
            GenerateSerialNumbersPage,
          )}
        />
        <Route
          path="/manage-codes"
          exact
          component={checkPageAccessPermissions(hasGroup(pageRoleGroups['generate-serials']), ManageCodesPage)}
        />
        <Route
          path="/manual"
          exact
          component={checkPageAccessPermissions(!hasGroup(pageRoleGroups.manual), ManualPage)}
        />
        <Route path="/no-permissions" exact component={() => <NoPermissions noGroups />} />
        <Redirect to={getGroupDefaultRoute()} />
      </Switch>
    </>
  )
})

export default Layout
